import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} QuantityRule
 * @property {number} min
 * @property {number|null} max
 * @property {number} increment
 */

/**
 * @typedef {Object} PriceBreak
 * @property {number} qty
 * @property {number} price
 * @property {string} price_formatted
 */

/**
 * @typedef {Object} VariantPricing
 * @property {number} id
 * @property {number} price
 * @property {string} price_formatted
 * @property {QuantityRule} rule
 * @property {PriceBreak[]} breaks
 */

/**
 * B2B volume pricing: table, quantity rules, and display-only price updates.
 * Listens to variant:update and quantity-selector:update on the product section.
 */
class B2bVolumePricing extends HTMLElement {
  /**
   * WPD and similar apps wrap | money output in HTML spans; strip before textContent updates.
   * @param {string} value
   * @returns {string}
   */
  static #stripHtml(value) {
    if (!value) return '';
    if (typeof DOMParser !== 'undefined') {
      const doc = new DOMParser().parseFromString(value, 'text/html');
      const text = doc.body.textContent?.trim();
      if (text) return text;
    }
    return value.replace(/<[^>]*>/g, '').trim();
  }

  /** @type {VariantPricing[]} */
  #variants = [];

  /** @type {number|null} */
  #currentVariantId = null;

  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {HTMLElement|null} */
  #quantityHint = null;

  connectedCallback() {
    this.#parseVariants();
    this.#currentVariantId = Number(this.dataset.currentVariantId) || null;

    const section = this.closest('.shopify-section');
    if (!section) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    section.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });

    const qtyInput = this.#getQuantityInput(section);
    const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

    this.#syncForVariant(this.#currentVariantId, quantity, false);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#quantityHint?.remove();
    this.#quantityHint = null;
  }

  #parseVariants() {
    const jsonEl = this.querySelector('[data-b2b-variants-json]');
    if (!jsonEl?.textContent) return;

    try {
      this.#variants = JSON.parse(jsonEl.textContent.trim());
    } catch (e) {
      console.error('B2B volume pricing: invalid variant JSON', e);
      this.#variants = [];
    }
  }

  /**
   * @param {number|null} variantId
   * @returns {VariantPricing|undefined}
   */
  #getVariant(variantId) {
    if (!variantId) return undefined;
    return this.#variants.find((v) => v.id === variantId);
  }

  /**
   * @param {Element} section
   * @returns {HTMLInputElement|null}
   */
  #getQuantityInput(section) {
    return section.querySelector('quantity-selector-component input[name="quantity"]');
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.productId && event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const variantId = event.detail.resource?.id ? Number(event.detail.resource.id) : null;
    if (!variantId) return;

    this.#currentVariantId = variantId;
    this.dataset.currentVariantId = String(variantId);

    const section = this.closest('.shopify-section');
    const qtyInput = section ? this.#getQuantityInput(section) : null;
    const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

    // Run after product-price morphs the price container on variant:update
    requestAnimationFrame(() => {
      this.#syncForVariant(variantId, quantity, true);
    });
  };

  /**
   * @param {import('@theme/events').QuantitySelectorUpdateEvent} event
   */
  #onQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    const section = this.closest('.shopify-section');
    if (!section) return;

    const variantId = this.#getActiveVariantId(section);
    if (variantId) {
      this.#currentVariantId = variantId;
    }

    const quantity = event.detail.quantity ?? 1;
    this.#syncForVariant(this.#currentVariantId, quantity, false);
  };

  /**
   * @param {Element} section
   * @returns {number|null}
   */
  #getActiveVariantId(section) {
    const hiddenId = section.querySelector('product-form-component input[name="id"], form[data-type="add-to-cart-form"] input[name="id"]');
    if (hiddenId instanceof HTMLInputElement && hiddenId.value) {
      return Number(hiddenId.value);
    }
    return this.#currentVariantId;
  }

  /**
   * @param {number|null} variantId
   * @param {number} quantity
   * @param {boolean} reRenderTable
   */
  #syncForVariant(variantId, quantity, reRenderTable) {
    const variant = this.#getVariant(variantId);
    if (!variant) return;

    const section = this.closest('.shopify-section');

    if (reRenderTable) {
      this.#renderTable(variant);
      this.#renderRules(variant);
    }

    this.#applyQuantityRules(section, variant);
    this.#validateQuantity(section, variant, quantity);

    const pricing = this.#resolvePrice(variant, quantity);
    this.#setActiveRow(pricing.activeQty);
    this.#updatePriceDisplay(section, pricing.priceFormatted);
    this.#updateHint(pricing, quantity);
    this.#toggleTableVisibility(variant);
  }

  /**
   * @param {VariantPricing} variant
   */
  #toggleTableVisibility(variant) {
    const root = this.querySelector('[data-b2b-pricing-root]');
    if (!root) return;

    const hasBreaks = variant.breaks && variant.breaks.length > 0;
    root.hidden = !hasBreaks;
    root.classList.toggle('b2b-volume-pricing--empty', !hasBreaks);
  }

  /**
   * @param {VariantPricing} variant
   */
  #renderTable(variant) {
    const tbody = this.querySelector('[data-b2b-pricing-tbody]');
    if (!tbody) return;

    const ruleMin = variant.rule?.min ?? 1;
    const rows = [];

    const baseQty = ruleMin > 1 ? ruleMin : 1;
    rows.push({
      qty: baseQty,
      price: variant.price,
      price_formatted: variant.price_formatted,
    });

    const breaks = [...(variant.breaks || [])]
      .filter((b) => b.qty !== baseQty)
      .sort((a, b) => a.qty - b.qty);
    for (const b of breaks) {
      rows.push(b);
    }

    tbody.innerHTML = rows
      .map(
        (row) => `
      <tr data-b2b-row data-b2b-qty="${row.qty}" data-b2b-price="${row.price}">
        <td>Ab ${row.qty} Stück</td>
        <td>${B2bVolumePricing.#stripHtml(row.price_formatted)}</td>
      </tr>`
      )
      .join('');
  }

  /**
   * @param {VariantPricing} variant
   */
  #renderRules(variant) {
    if (this.dataset.showQuantityRuleInfo !== 'true') return;

    const rulesEl = this.querySelector('[data-b2b-rules]');
    if (!rulesEl) return;

    const rule = variant.rule ?? { min: 1, max: null, increment: 1 };
    const parts = [];

    if (rule.min > 1) {
      parts.push(`<span data-b2b-rule-min>Mindestmenge: ${rule.min}</span>`);
    }
    if (rule.max) {
      parts.push(`<span data-b2b-rule-max>Maximalmenge: ${rule.max}</span>`);
    }
    if (rule.increment > 1) {
      parts.push(`<span data-b2b-rule-increment>Schrittweite: ${rule.increment}</span>`);
    }

    rulesEl.innerHTML = parts.join('');
    rulesEl.hidden = parts.length === 0;
  }

  /**
   * @param {Element|null} section
   * @param {VariantPricing} variant
   */
  #applyQuantityRules(section, variant) {
    if (!section) return;

    const input = this.#getQuantityInput(section);
    if (!input) return;

    const rule = variant.rule ?? { min: 1, max: null, increment: 1 };

    input.min = String(rule.min);
    input.step = String(rule.increment);

    if (rule.max) {
      input.max = String(rule.max);
    } else {
      input.removeAttribute('max');
    }
  }

  /**
   * @param {Element|null} section
   * @param {VariantPricing} variant
   * @param {number} quantity
   */
  #validateQuantity(section, variant, quantity) {
    if (!section) return;

    const rule = variant.rule ?? { min: 1, max: null, increment: 1 };
    const messages = [];

    if (quantity < rule.min) {
      messages.push(`Mindestmenge: ${rule.min}`);
    }
    if (rule.max && quantity > rule.max) {
      messages.push(`Maximalmenge: ${rule.max}`);
    }
    if (rule.increment > 1 && (quantity - rule.min) % rule.increment !== 0) {
      messages.push(`Menge muss in Schritten von ${rule.increment} erhöht werden`);
    }

    const qtyComponent = section.querySelector('quantity-selector-component');
    if (!qtyComponent) return;

    if (!this.#quantityHint) {
      this.#quantityHint = document.createElement('p');
      this.#quantityHint.className = 'b2b-quantity-hint';
      this.#quantityHint.setAttribute('role', 'status');
      qtyComponent.insertAdjacentElement('afterend', this.#quantityHint);
    }

    if (messages.length > 0) {
      this.#quantityHint.textContent = messages.join(' · ');
      this.#quantityHint.hidden = false;
    } else {
      this.#quantityHint.textContent = '';
      this.#quantityHint.hidden = true;
    }
  }

  /**
   * Highest applicable break: max break.qty where break.qty <= quantity; else base price.
   * @param {VariantPricing} variant
   * @param {number} quantity
   */
  #resolvePrice(variant, quantity) {
    const ruleMin = variant.rule?.min ?? 1;
    let activeQty = ruleMin > 1 ? ruleMin : 1;
    let price = variant.price;
    let priceFormatted = variant.price_formatted;

    const breaks = [...(variant.breaks || [])].sort((a, b) => a.qty - b.qty);

    for (const b of breaks) {
      if (b.qty <= quantity) {
        activeQty = b.qty;
        price = b.price;
        priceFormatted = b.price_formatted;
      }
    }

    return { activeQty, price, priceFormatted };
  }

  /**
   * @param {number} activeQty
   */
  #setActiveRow(activeQty) {
    const rows = this.querySelectorAll('[data-b2b-row]');
    rows.forEach((row) => {
      const qty = Number(row.getAttribute('data-b2b-qty'));
      const isActive = qty === activeQty;
      row.toggleAttribute('data-b2b-row-active', isActive);
      row.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  /**
   * Display-only price update in product-price block.
   * @param {Element|null} section
   * @param {string} priceFormatted
   */
  #updatePriceDisplay(section, priceFormatted) {
    if (!section) return;

    const priceSpan = section.querySelector(`product-price[data-product-id="${this.dataset.productId}"] .price`);
    if (priceSpan) {
      priceSpan.textContent = B2bVolumePricing.#stripHtml(priceFormatted);
    }
  }

  /**
   * @param {{ priceFormatted: string }} pricing
   * @param {number} quantity
   */
  #updateHint(pricing, quantity) {
    if (this.dataset.showCurrentPriceHint !== 'true') return;

    const hintQty = this.querySelector('[data-b2b-hint-qty]');
    const hintPrice = this.querySelector('[data-b2b-hint-price]');
    if (hintQty) hintQty.textContent = String(quantity);
    if (hintPrice) hintPrice.textContent = B2bVolumePricing.#stripHtml(pricing.priceFormatted);
  }
}

if (!customElements.get('b2b-volume-pricing')) {
  customElements.define('b2b-volume-pricing', B2bVolumePricing);
}
